if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

/**
 * Measure the stacked header so the homepage hero can fill the
 * remaining viewport. Header is static — no sticky / is-scrolled.
 */
function syncDtfHeaderHeight() {
  const wrapper = document.querySelector('.header-group-wrapper');
  if (!wrapper) return;
  const h = Math.round(wrapper.getBoundingClientRect().height);
  if (h > 40) {
    document.documentElement.style.setProperty('--dtf-header-h', h + 'px');
  }
}
window.dtfSyncHeaderHeight = syncDtfHeaderHeight;

function initHeaderHeight() {
  const wrapper = document.querySelector('.header-group-wrapper');
  if (!wrapper) return;

  syncDtfHeaderHeight();
  wrapper.querySelectorAll('.logoimg').forEach((img) => {
    if (!img.complete) img.addEventListener('load', syncDtfHeaderHeight, { once: true });
  });
  window.addEventListener('resize', debounce(syncDtfHeaderHeight, 100), { passive: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderHeight);
} else {
  initHeaderHeight();
}

/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
      this.scrollPosition = 0;
    }
    
    connectedCallback() {
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');

      // Escape key handler
      document.addEventListener('keyup', (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          if (this.toggle && this.toggle.classList.contains('active')) {
            this.closeMenu();
          }
        }
      });

      // Mobile toggle click handler (only if toggle exists)
      if (this.toggle) {
        const mobileToggleBtn = this.toggle.querySelector('.mobile-toggle');
        if (mobileToggleBtn) {
          mobileToggleBtn.addEventListener('click', (e) => {
            // Always prevent default to stop auto-scroll behavior
            e.preventDefault();
            e.stopPropagation();
            
            if (this.toggle.classList.contains('active')) {
              this.closeMenu();
            } else {
              this.openMenu();
            }
            window.dispatchEvent(new Event('resize.resize-select'));
          });
        }
        
        // Prevent details toggle event from causing scroll
        this.toggle.addEventListener('toggle', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      }

      // Mobile menu buttons
      if (this.menu) {
        this.menu.querySelectorAll('summary').forEach(summary => {
          summary.addEventListener('click', this.onSummaryClick.bind(this));
        });
        const overlayClose = this.menu.querySelector('.mobile-menu-close');
        if (overlayClose) {
          overlayClose.addEventListener('click', (e) => {
            e.preventDefault();
            this.closeMenu();
          });
        }
      }
    }

    disconnectedCallback() {
      if (this.menu && this._menuHome && this.menu.parentElement !== this._menuHome) {
        this._menuHome.appendChild(this.menu);
      }
      this._menuHome = null;
    }

    openMenu() {
      if (this.menu) {
        if (this.menu.parentElement !== document.body) {
          this._menuHome = this.menu.parentElement;
          document.body.appendChild(this.menu);
        }
        this.menu.classList.add('active');
        this.menu.style.top = '';
        this.menu.style.height = '';
        this.menu.style.maxHeight = '';
        const closeBtn = this.menu.querySelector('.mobile-menu-close');
        if (closeBtn) closeBtn.focus();
      }

      this.toggle.setAttribute('open', '');
      document.body.classList.add('overflow-hidden', 'dtf-nav-open');
      document.documentElement.classList.add('dtf-nav-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      setTimeout(() => {
        this.toggle.classList.add('active');
      });
    }

    closeMenu() {
      this.toggle.classList.remove('active');

      document.body.classList.remove('overflow-hidden', 'dtf-nav-open');
      document.documentElement.classList.remove('dtf-nav-open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      if (this.menu) {
        this.menu.classList.remove('active');
        this.menu.style.top = '';
        this.menu.style.height = '';
        if (this._menuHome && this.menu.parentElement !== this._menuHome) {
          this._menuHome.appendChild(this.menu);
        }
        this._menuHome = null;
      }

      this.closeAnimation(this.toggle);
    }

    onSummaryClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const detailsElement = event.currentTarget.parentNode;
      const isOpen = detailsElement.hasAttribute('open');
      const menuRoot = detailsElement.closest('.mobile-menu');
      const isTopLevel = menuRoot && detailsElement.parentElement.parentElement === menuRoot;

      if (isOpen) {
        detailsElement.classList.remove('submenu-open');
        detailsElement.removeAttribute('open');
        return;
      }

      if (isTopLevel) {
        menuRoot.querySelectorAll(':scope > li > details[open]').forEach((openItem) => {
          openItem.classList.remove('submenu-open');
          openItem.removeAttribute('open');
        });
      }

      detailsElement.classList.add('submenu-open');
      detailsElement.setAttribute('open', '');
    }

    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
      this.submenus = this.querySelectorAll('.thb-full-menu .menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
    }
    
    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      document.fonts.ready.then(function () {
        window.addEventListener('resize', debounce(function () {
          _this.resizeSubMenus();
        }, 100));
        _this.resizeSubMenus();
      });
    }
    
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.offsetWidth,
            total = w + l;

          if (total > window.innerWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}

/* Chat links open Tawk.to. Tawk boots async, so clicks before it loads
 * retry briefly; if it never shows up, tell the visitor where the corner
 * button is and give them the phone line. */
(function () {
  function openTawk() {
    const api = window.Tawk_API;
    if (!api) return false;
    if (typeof api.maximize === 'function') {
      try { api.maximize(); return true; } catch (e) { /* fall through */ }
    }
    if (typeof api.toggle === 'function') {
      try { api.toggle(); return true; } catch (e) { /* fall through */ }
    }
    return false;
  }

  function showChatHelp() {
    if (document.querySelector('.town-chat-help')) return;
    const phone = document.querySelector('a[href^="tel:"]');
    const value = phone && phone.querySelector('.header-utility-value');
    const esc = function (s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    };
    const number = esc(value ? value.textContent.replace(/\s+/g, ' ').trim() : '+1 (832) 406-4434');
    const href = esc(phone ? phone.getAttribute('href') : 'tel:+18324064434');
    const layer = document.createElement('div');
    layer.className = 'town-chat-help';
    layer.setAttribute('role', 'dialog');
    layer.setAttribute('aria-modal', 'true');
    layer.setAttribute('aria-labelledby', 'town-chat-help-title');
    layer.innerHTML =
      '<div class="town-chat-help__card">' +
        '<p class="town-chat-help__title" id="town-chat-help-title">Chat is in the bottom right</p>' +
        '<p>Click the <strong>Chat</strong> button in the corner of this page. If you do not see it, call <a href="' + href + '">' + number + '</a>.</p>' +
        '<button type="button" class="town-chat-help__close">Okay</button>' +
      '</div>';
    function close() {
      layer.remove();
      document.removeEventListener('keydown', onKey);
    }
    function onKey(event) {
      if (event.key === 'Escape') close();
    }
    layer.addEventListener('click', function (event) {
      if (event.target === layer || event.target.closest('.town-chat-help__close')) close();
    });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(layer);
    const closeBtn = layer.querySelector('.town-chat-help__close');
    if (closeBtn) closeBtn.focus();
  }

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest(
      'a[href="#tawk-chat"], [data-tawk-trigger="true"], [data-open-tawkto-live-support="true"], .main-store-section__chat-btn'
    );
    if (!trigger) return;
    event.preventDefault();
    if (openTawk()) return;
    let tries = 0;
    const timer = setInterval(function () {
      tries += 1;
      if (openTawk()) {
        clearInterval(timer);
        return;
      }
      if (tries >= 8) {
        clearInterval(timer);
        showChatHelp();
      }
    }, 500);
  });
})();
